<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
import { useLogger } from 'vue-logger-plugin'

import { Button as KButton } from '@progress/kendo-vue-buttons'
import { SubmitInvoice } from '../services/CartService.ts'
import type { IAoeCart, IAoeInvoiceSubmitResponse, IAoeInvoiceResponse } from '../types/CartTypes.ts'

import AoeCartItemGrid from '../components/Controls/AoeCartItemGrid.vue'
import AoeOrderTotalsSummary from '../components/Controls/AoeOrderTotalsSummary.vue'
import AoePaymentControl from '../components/Controls/AoePaymentControl.vue'
import AoeInvoiceOrderSummary from '../components/Controls/AoeInvoiceOrderSummary.vue'

import Swal from "sweetalert2";
import { SetIsLoading } from '../services/AoeHelpers'
import { handleError } from '../services/ErrorService'
import { ref } from 'vue'

const logger = useLogger()

const props = defineProps<{
  invoiceCart: IAoeInvoiceResponse | null
  cart: IAoeCart | null
  productImageUrls: Record<string, string>
  invoicenumber: string
  rootUrl: string
  emailAddress: string
}>()

  logger.debug('AoeInvoicePayment','props', props)

const emit = defineEmits<{
  'update-cart': []
}>()

const invoiceSubmitResp = ref<IAoeInvoiceSubmitResponse | null>(null)

function updateCartHandler(event: MouseEvent) {
  logger.debug('updateCartHandler', event)
  emit('update-cart')
}

async function submitInvoice(event: MouseEvent) {
  logger.debug('submitInvoice', event)

  if (!props.invoicenumber) return

  try {
    SetIsLoading(true)
    const data = await SubmitInvoice({ invoiceNumber: props.invoicenumber, emailAddress: props.emailAddress })

    if (data != null)
    {
      invoiceSubmitResp.value = data
      if (invoiceSubmitResp.value != null && invoiceSubmitResp.value.invoiceSubmitErrors != null)
      {
        Swal.fire({
          title: t("alerts.SubmitInvoiceError"),
          text: t("alerts.SubmitInvoiceErrorSubTitle", [invoiceSubmitResp.value.invoiceSubmitErrors[0] ]),
          icon: "warning",
          confirmButtonText: t("buttons.Okay"),
        })
      }
      else
      {
        if (invoiceSubmitResp.value != null && invoiceSubmitResp.value.orderSubmitted)
        {
          Swal.fire({
            title: t("alerts.SubmitInvoiceSuccess"),
            text: t("alerts.SubmitInvoiceSuccessWithOrder"),
            icon: "success",
            confirmButtonText: t("buttons.Okay"),
          })
          .then((result) => {
            if (result.isConfirmed) {
              logger.debug('AoeInvoicePayment','submitInvoice redirecting to rootUrl', props.rootUrl)
              window.location.href = props.rootUrl
            }
          })
        }
        else
        {
          Swal.fire({
            title: t("alerts.SubmitInvoiceSuccess"),
            text: t("alerts.SubmitInvoiceSuccessWithOutOrder"),
            icon: "success",
            confirmButtonText: t("buttons.Okay"),
          })
          .then((result) => {
            if (result.isConfirmed) {
              logger.debug('AoeInvoicePayment','submitInvoice redirecting to rootUrl', props.rootUrl)
              window.location.href = props.rootUrl
            }
          })
        }
      }
    }
  } catch (error) {
    logger.error('SubmitInvoice error', error)
    await handleError(error, t)
  } finally {
    SetIsLoading(false)
  }
}

</script>

<style lang="scss">
/* Updated styling to match application design patterns */
.aoe-invoice-payment {
  color: #333;
}

/* Global header styling for all components */
.section-header-div,
.party-order-entry-section-header-div {
  @include typography-body-large;
  @include font-weight-semibold;
  padding: $spacing-3 $spacing-4;
  background-color: transparent;
  border-bottom: 2px solid var(--primary-text-color, var(--vt-c-teal-dark));
  margin-bottom: 0;
}

.invoice-section-header,
.shopping-cart-section-header,
.order-summary-section-header,
.payment-control-section-header {
  color: var(--primary-text-color, var(--vt-c-teal-dark));
  letter-spacing: 0.5px;
}

.invoice-instruction-div {
  padding: $spacing-4;
  background-color: var(--vt-c-white-soft);
  border-radius: 0 0 6px 6px;
  margin-bottom: $spacing-4;
  box-shadow: 0 2px 4px black;
}

.invoice-instr-label {
  @include typography-small;
  line-height: 1.5;
  color: #555;
}

.invoice-order-summary-div,
.invoice-cart-item-grid-div,
.aoe-order-summary-div,
.aoe-payment-control-div {
  margin-bottom: 20px;
  border-radius: $radius-md;
  box-shadow: 0 2px 8px rgba(black, 0.1);
  overflow: hidden;
}

.aoe-invoice-submit-div {
  margin: $spacing-8 0;
}

.invoice-submit-button {
  @include font-weight-medium;
  min-width: 150px;
  border-radius: $button-radius-lg;
}
</style>

<template>
  <div class="aoe-invoice-payment">
    <div class="row section-header-row">
      <div class="col-12 section-header-div invoice-section-header">
        {{ t('invoice.InvoiceHeader') }}
      </div>
    </div>
    <div class="row mt-3">
      <div class="col-12 invoice-instruction-div">
        <span class="invoice-instr-label" v-html="t('invoice.InvoiceInstruction')"></span>
      </div>
    </div>
    <div class="row mt-3">
      <div class="col-12 invoice-order-summary-div">
        <AoeInvoiceOrderSummary
          :invoicecart="invoiceCart"
        ></AoeInvoiceOrderSummary>
      </div>
    </div>
    <div class="row mt-3">
      <div class="col-12 invoice-cart-item-grid-div">
        <AoeCartItemGrid
          :cart="cart"
          :productimageurls="productImageUrls"
          :editable="false"
        ></AoeCartItemGrid>
      </div>
    </div>
    <div class="row mt-3">
      <div class="col-12 aoe-order-summary-div">
        <AoeOrderTotalsSummary
          :cart="cart"
        ></AoeOrderTotalsSummary>
      </div>
    </div>
    <div class="row mt-3">
      <div class="col-12 aoe-payment-control-div">
        <AoePaymentControl
          :carts="cart != null ? [cart] : [{} as IAoeCart]"
          :showdeletebutton="true"
          :showinvoice="false"
          :allowaddpayment="true"
          :excludedControlFolderNames="['OPA']"
          @update-cart="updateCartHandler($event)"
        ></AoePaymentControl>
      </div>
    </div>
    <div v-if="cart?.balance == 0" class="row mt-3">
      <div  class="col-12 aoe-invoice-submit-div text-center">
          <KButton
            :rounded="'large'"
            class="baseBtn primaryBtn invoice-submit-button"
            @click="submitInvoice($event)">
            {{ t('invoice.SubmitButton') }}
          </KButton>
      </div>
    </div>
  </div>
</template>
